import React from 'react';

// The 19 need tiles — what guests ask for, in the order the database sends them.
// One tap asks. The active tile carries the brand tint so the desk can see what
// the answers below are answering. Tiles are grouped by their `kind` field so the
// wall reads as Meals / Things to do / Errands & Services instead of one flat
// list. Any `kind` the mapping below doesn't know falls into an "Others" group at
// the bottom, so a new backend kind appears on its own without a frontend change.

// kind → group label. Add a row here when the backend sends a new kind; tiles
// whose `kind` isn't listed land in "Others" automatically (see groupTiles).
const KIND_TO_GROUP = {
  meal: 'Meals',
  outing: 'Things to do',
  errand: 'Errands & Services',
};
const OTHERS_LABEL = 'Others';
// Display order of the known groups; "Others" always renders last.
const GROUP_ORDER = ['meal', 'outing', 'errand'];

// Inline stroke glyphs (Feather/Lucide style, matching the repo's other inline
// icon components — no icon library is installed). One per tile, keyed by
// `need_key`. Falls back to a map-pin for any tile without a dedicated glyph
// (same pattern as OccasionTiles). Each glyph uses currentColor, so the icon
// inherits the button's text color — blue when active, gray otherwise.
const Glyph = ({ children, className = '' }) => (
  <svg className={`w-4 h-4 shrink-0 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const PinGlyph = () => (
  <Glyph>
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
    <circle cx="12" cy="10" r="3" />
  </Glyph>
);

const NEED_ICONS = {
  // Meals
  dinner: (<Glyph><path d="M4 3v6a2 2 0 0 0 2 2 2 2 0 0 0 2-2V3" /><path d="M6 11v10" /><path d="M18 3v18" /></Glyph>),
  breakfast: (<Glyph><path d="M18 8h1a4 4 0 0 1 0 8h-1" /><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" /><line x1="6" y1="2" x2="6" y2="4" /><line x1="10" y1="2" x2="10" y2="4" /><line x1="14" y1="2" x2="14" y2="4" /></Glyph>),
  lunch: (<Glyph><path d="M5 3v6a2 2 0 0 0 4 0V3" /><path d="M7 9v12" /></Glyph>),
  cheap_eats: (<Glyph><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></Glyph>),
  drinks: (<Glyph><path d="M17 11h1a3 3 0 0 1 0 6h-1" /><path d="M4 6h12v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6z" /><path d="M8 2c-.5 1 .5 2 0 3" /><path d="M12 2c-.5 1 .5 2 0 3" /></Glyph>),
  dessert: (<Glyph><path d="M7.5 11h9l-2 9a1 1 0 0 1-1 .8h-3a1 1 0 0 1-1-.8l-2-9z" /><path d="M12 11V8" /><circle cx="12" cy="6" r="3.2" /></Glyph>),
  // Outings
  rainy_day: (<Glyph><path d="M20 16.58A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" /><line x1="8" y1="19" x2="8" y2="21" /><line x1="16" y1="19" x2="16" y2="21" /><line x1="12" y1="21" x2="12" y2="23" /></Glyph>),
  kids: (<Glyph><circle cx="12" cy="8" r="4" /><path d="M5 21v-1a7 7 0 0 1 14 0v1" /></Glyph>),
  hike: (<Glyph><polyline points="2 21 8 9 12 16 16 11 22 21" /></Glyph>),
  water: (<Glyph><path d="M2 12c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2" /><path d="M2 17c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2" /><path d="M2 7c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2" /></Glyph>),
  sunset: (<Glyph><path d="M17 18a5 5 0 0 0-10 0" /><line x1="12" y1="9" x2="12" y2="2" /><line x1="4.22" y1="10.22" x2="5.64" y2="11.64" /><line x1="1" y1="18" x2="3" y2="18" /><line x1="21" y1="18" x2="23" y2="18" /><line x1="18.36" y1="11.64" x2="19.78" y2="10.22" /><line x1="23" y1="22" x2="1" y2="22" /><polyline points="16 5 12 9 8 5" /></Glyph>),
  dog_friendly: (<Glyph><circle cx="5.5" cy="10" r="1.8" /><circle cx="10.5" cy="6" r="1.8" /><circle cx="15" cy="6" r="1.8" /><circle cx="19.5" cy="10" r="1.8" /><path d="M8 16a4 4 0 0 1 8 0 2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2z" /></Glyph>),
  attractions: (<Glyph><circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" /></Glyph>),
  live_music: (<Glyph><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></Glyph>),
  shopping: (<Glyph><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></Glyph>),
  // Errands
  hardware: (<Glyph><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94z" /></Glyph>),
  grocery: (<Glyph><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></Glyph>),
  fuel: (<Glyph><path d="M3 22h12" /><rect x="4" y="2" width="9" height="20" rx="1" /><line x1="4" y1="9" x2="13" y2="9" /><path d="M14 12h2.5a2 2 0 0 1 2 2V17a1.5 1.5 0 0 0 3 0V9.5L17 6" /></Glyph>),
  laundry: (<Glyph><path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z" /></Glyph>),
};

const needIcon = (key) => NEED_ICONS[key] || <PinGlyph />;

// Partition the tile list into ordered groups. Known kinds follow GROUP_ORDER;
// any kind not in KIND_TO_GROUP is pooled into "Others" (rendered last). Within
// each group the database's original order is preserved (Map insertion order +
// a single pass keeps it stable).
const groupTiles = (tiles) => {
  const byKind = new Map();
  for (const t of tiles) {
    const k = t.kind || '';
    if (!byKind.has(k)) byKind.set(k, []);
    byKind.get(k).push(t);
  }
  const groups = [];
  for (const k of GROUP_ORDER) {
    const items = byKind.get(k);
    if (items && items.length) groups.push({ label: KIND_TO_GROUP[k], items });
  }
  const others = [];
  for (const [k, items] of byKind) {
    if (!KIND_TO_GROUP[k]) others.push(...items);
  }
  if (others.length) groups.push({ label: OTHERS_LABEL, items: others });
  return groups;
};

// Pick a column count that divides the tile count, so the last row is full (no
// empty half-row). Largest divisor in [2..5]; fall back to min(n, 5) for counts
// with no divisor in that range (e.g. primes). Mobile always stays at 2 cols.
const colsForCount = (n) => {
  for (let c = 5; c >= 2; c -= 1) if (n % c === 0) return c;
  return Math.max(2, Math.min(n, 5));
};
// Literal Tailwind class strings — the JIT purge can't see interpolated names,
// so every column count we hand back has its own full class string here.
const GRID_BY_COLS = {
  2: 'grid grid-cols-2 gap-2 items-stretch',
  3: 'grid grid-cols-2 sm:grid-cols-3 gap-2 items-stretch',
  4: 'grid grid-cols-2 sm:grid-cols-4 gap-2 items-stretch',
  5: 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 items-stretch',
};

// Matches the FILTERS / PARTY / ACCESS / WHEN uppercase small-label treatment.
const GROUP_LABEL = 'text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500';

const NeedTiles = ({ tiles, activeNeed, onPick }) => {
  const list = Array.isArray(tiles) ? tiles : [];
  if (list.length === 0) return null;
  const groups = groupTiles(list);
  return (
    <div className="space-y-4" role="group" aria-label="Guest needs">
      {groups.map((g) => (
        <div key={g.label} className="space-y-2">
          <div className={GROUP_LABEL}>{g.label}</div>
          <div className={GRID_BY_COLS[colsForCount(g.items.length)] || GRID_BY_COLS[2]}>
            {g.items.map((t) => {
              const active = activeNeed === t.need_key;
              return (
                <button
                  key={t.need_key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onPick(t)}
                  className={`min-h-[44px] px-3 py-2 text-sm font-medium rounded-lg border text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/50 ${
                    active
                      ? 'border-[#32B1FF] bg-[#32B1FF]/10 text-[#32B1FF]'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 hover:border-[#32B1FF]/60 hover:bg-[#32B1FF]/5'
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="shrink-0">{needIcon(t.need_key)}</span>
                    <span className="truncate">{t.label}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default NeedTiles;
